import React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiHome, FiPackage, FiTrendingUp, FiTruck, FiDollarSign, FiSettings, FiX, FiTag, FiBarChart2} = FiIcons;

const Sidebar = ({isOpen, onClose}) => {
  const location = useLocation();

  const navigation = [
    {name: 'Dashboard', href: '/', icon: FiHome},
    {name: 'Inventory Control', href: '/inventory', icon: FiPackage},
    {name: 'Stock & Pricing', href: '/stock-pricing', icon: FiTag},
    {name: 'WhatsApp Bot', href: '/whatsapp-bot', icon: FiIcons.FiMessageSquare},
    {name: 'Sales Pipeline', href: '/sales', icon: FiTrendingUp},
    {name: 'Dispatch & Fulfillment', href: '/dispatch', icon: FiTruck},
    {name: 'Financial Reports', href: '/financial', icon: FiDollarSign},
    {name: 'Automation Savings', href: '/savings', icon: FiBarChart2},
    {name: 'Settings', href: '/settings', icon: FiSettings},
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0a0f0d] border-r border-[#243129] transform transition-transform duration-300 lg:translate-x-0 lg:static lg:inset-0 h-screen flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:transform-none`}
      >
        {/* Logo Header */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-[#243129]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-lime-400 to-lime-600 flex items-center justify-center shadow-lg shadow-lime-500/20">
              <SafeIcon icon={FiPackage} className="w-5 h-5 text-[#050807]" />
            </div>
            <div className="flex flex-col">
              <span className="font-display text-lg font-semibold text-[#e8f5e9] tracking-tight">Fresh Board</span>
              <span className="text-[10px] font-mono text-[#6b8772] uppercase tracking-widest">Operations</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-md text-[#6b8772] hover:text-[#84cc16] hover:bg-[#1a2420] transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {navigation.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03, duration: 0.3 }}
              >
                <Link
                  to={item.href}
                  onClick={onClose}
                  className={`group flex items-center px-3 py-2.5 text-sm font-mono rounded-lg transition-all duration-200 relative ${
                    isActive(item.href)
                      ? 'bg-[#1a2420] text-[#84cc16]'
                      : 'text-[#a5c4ad] hover:text-[#e8f5e9] hover:bg-[#111916]'
                  }`}
                >
                  {/* Active indicator line */}
                  {isActive(item.href) && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-lime-400 rounded-r"
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                  <SafeIcon
                    icon={item.icon}
                    className={`mr-3 h-4 w-4 transition-colors ${
                      isActive(item.href) ? 'text-[#84cc16]' : 'text-[#6b8772] group-hover:text-[#a5c4ad]'
                    }`}
                  />
                  <span className="truncate">{item.name}</span>
                  {/* Hover glow */}
                  {isActive(item.href) && (
                    <div className="absolute inset-0 rounded-lg bg-lime-500/5 pointer-events-none" />
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        </nav>

        {/* Status indicator */}
        <div className="p-4 border-t border-[#1c2822]">
          <div className="bg-[#111916] border border-[#243129] rounded-lg p-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="w-2 h-2 bg-lime-400 rounded-full" />
                <div className="absolute inset-0 w-2 h-2 bg-lime-400 rounded-full animate-ping opacity-50" />
              </div>
              <span className="text-xs font-mono font-medium text-[#84cc16]">SYSTEM ONLINE</span>
            </div>
            <p className="text-[10px] font-mono text-[#6b8772] mt-1.5 tracking-wide">All integrations active</p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
